import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import Image from 'next/image';
import { nanoid } from 'nanoid';
import constants from '../../../constants/constants';
import AlertMessage from '../../global/alert-message/AlertMessage';
import getBreakpointFlags from '../../../helpers/get-breakpoint-flags';
import LinkButton from '../../global/linkButton/linkButton';
import PDPContentModal from '../pdp-content-modal/PdpContentModal';
import PdpContentWellModel from '../../../models/pdp-content-well';
import PdpValueTile from '../pdp-value-tile/PdpValueTile';
import RichtextModel from '../../../models/richtext';
import styles from './pdp-content-well.module.scss';
import useBread from '../../../hooks/useBread';
import useWindowSize from '../../../hooks/useWindowSize';
import ARQuickLook from '../../global/ar-quick-look/ArQuickLook';
import Link from '../../../models/link';
import Checkmark from '../../../public/svg/checkmark.svg';
import RichText from '../../global/richtext/Richtext';

type Props = {
  data: PdpContentWellModel | undefined;
};

const PDPContentWell = ({ data }: Props) => {
  if (!data) return <></>;

  const {
    price,
    priceSubtextLink,
    description,
    secondLine,
    secondLineSubtext,
    cta,
    breadErrorMessage,
    estimatedDeliveryDays,
    estimatedDeliveryText,
    eqxIcon,
    membershipPrice,
    membershipPriceText,
    miniBoxes,
    pdpQuickLook,
    warrantyText,
    warrantyIcon,
    warrantyItems,
    promo
  } = data;

  const parsedPrice = Number(price?.replace(/[^0-9.]/g, ''));
  const breadWrapperRef = useRef(null);
  const { breadError, hideError } = useBread(
    breadWrapperRef,
    promo?.price || parsedPrice
  );

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<RichtextModel | undefined>(
    undefined
  );
  const [modalKey, setModalKey] = useState(nanoid());
  const [warrantyId, setWarrantyId] = useState<string>('');

  const promoPrice = Number(parsedPrice) - Number(promo?.promoDiscountedAmount);
  const displayPrice = promo?.priceTemplate?.replace(
    constants.priceTemplateString,
    promoPrice.toLocaleString('en')
  );

  useEffect(() => {
    setModalKey(nanoid());
  }, [modalContent]);

  const { width } = useWindowSize();
  const { isMobile, isTablet } = getBreakpointFlags(width);

  const renderDeliveryDate = () => {
    const today = new Date();
    const estimatedDeliveryDate = estimatedDeliveryDays
      ? new Date(today.setDate(today.getDate() + estimatedDeliveryDays))
      : null;
    return estimatedDeliveryDate?.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric'
    }); // months from 1-12
  };

  const getCtaLinkData = () => {
    const warrantyItem = warrantyItems?.find(
      (item) => item.variantId === warrantyId
    );
    const extendConfig =
      warrantyItem && !warrantyItem.variantId.includes('free')
        ? btoa(JSON.stringify(warrantyItem.config))
        : null;

    return new Link({
      ...cta,
      ...(extendConfig && {
        route: cta?.route.includes('?')
          ? `${cta?.route}&extend=${extendConfig}`
          : `${cta?.route}?extend=${extendConfig}`
      })
    });
  };

  return (
    <div
      className={cx({
        [styles['pdp-content-well__content-container']]: !promo,
        [styles['pdp-content-well__content-container-promo']]: promo
      })}
    >
      <div
        className={cx({
          [styles['pdp-content-well__large-box']]: !promo,
          [styles['pdp-content-well__large-box-promo']]: promo
        })}
      >
        {promo && (
          <p className={styles['pdp-content-well__promo-name']}>
            <Checkmark />
            {promo.pdpLabel} {promo.promoCode}
          </p>
        )}
        <div className={styles['pdp-content-well__price-container']}>
          {promo ? (
            <>
              <span className={styles['pdp-content-well__price-text-promo']}>
                {displayPrice}
              </span>
              <span
                className={styles['pdp-content-well__price-text-line-through']}
              >
                ${parsedPrice.toLocaleString('en')}
              </span>
            </>
          ) : (
            <p className={styles['pdp-content-well__price-text']}>{price}</p>
          )}

          {priceSubtextLink && (
            <div
              className={cx({
                [styles['pdp-content-well__price-subtext-container']]: !promo,
                [styles['pdp-content-well__price-subtext-container-promo']]:
                  promo
              })}
              id="bread-checkout-btn"
              ref={breadWrapperRef}
            >
              {breadError && (
                <div className={styles['pdp-content-well__bread-error']}>
                  <AlertMessage
                    {...breadErrorMessage}
                    onClose={hideError}
                    arrow="topmid"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {description && (
          <RichText
            data={description}
            customClasses={{
              container: styles['pdp-content-well__description'],
              header: styles['pdp-content-well__second-line'],
              header2: styles['pdp-content-well__second-line'],
              paragraph: styles['pdp-content-well__second-line'],
              link: styles['pdp-content-well__second-line']
            }}
          />
        )}
        {secondLine && (
          <p
            className={cx(
              styles['pdp-content-well__row'],
              styles['pdp-content-well__second-line']
            )}
          >
            {secondLine}
          </p>
        )}
        {secondLineSubtext && (
          <RichText
            data={secondLineSubtext}
            customClasses={{
              container:
                styles['pdp-content-well__second-line-subtext__container'],
              header: styles['pdp-content-well__second-line-subtext__text'],
              header2: styles['pdp-content-well__second-line-subtext__text'],
              paragraph: styles['pdp-content-well__second-line-subtext__text'],
              link: styles['pdp-content-well__second-line-subtext__text']
            }}
          />
        )}

        {warrantyText && (
          <span className={styles['pdp-content-well__warranty-text-container']}>
            {warrantyText}
            {warrantyIcon && (
              <span className={styles['pdp-content-well__warranty-icon']}>
                <Image
                  alt={warrantyIcon.alt}
                  layout="fill"
                  objectFit="contain"
                  src={warrantyIcon.src}
                />
              </span>
            )}
          </span>
        )}
        {warrantyItems && warrantyItems.length > 0 && (
          <div className={styles['pdp-content-well__warranty-items-container']}>
            {warrantyItems.map((warrantyItem) => (
              <button
                className={cx(styles['pdp-content-well__warranty-item'], {
                  [styles['pdp-content-well__warranty-item--selected']]:
                    warrantyId === warrantyItem.variantId
                })}
                key={warrantyItem.variantId}
                onClick={() =>
                  setWarrantyId(
                    warrantyId === warrantyItem.variantId
                      ? ''
                      : warrantyItem.variantId
                  )
                }
                type="button"
                name="warranty item"
              >
                <div>{warrantyItem.title}</div>
                <div
                  className={styles['pdp-content-well__warranty-item-price']}
                >
                  {warrantyItem.price}
                </div>
              </button>
            ))}
          </div>
        )}
        <div className={styles['pdp-content-well__cta-container']}>
          <LinkButton
            linkClass={cx({
              [styles['pdp-content-well__cta']]: !promo,
              [styles['pdp-content-well__cta-promo']]: promo
            })}
            data={getCtaLinkData()}
          />

          <p className={styles['pdp-content-well__delivery-text']}>
            {estimatedDeliveryText} {renderDeliveryDate()}
          </p>
        </div>
        <div className={styles['pdp-content-well__membership-info']}>
          <div
            className={styles['pdp-content-well__membership-icon-container']}
          >
            {eqxIcon && (
              <Image
                alt={eqxIcon.alt}
                className={styles['pdp-content-well__membership-icon']}
                height={eqxIcon.height}
                src={eqxIcon.src}
                width={eqxIcon.width}
              />
            )}
          </div>

          <p
            className={cx({
              [styles['pdp-content-well__membership-text']]: !promo,
              [styles['pdp-content-well__membership-text-promo']]: promo
            })}
          >
            <span className={styles['pdp-content-well__membership-price']}>
              {membershipPrice}
            </span>
            {membershipPriceText}
          </p>
        </div>
        <div className={styles['pdp-content-well__accrue']}>
          <span className="pdp-content-well__accrue" />
        </div>
      </div>

      <div className={styles['pdp-content-well__small-box-container']}>
        <div className={styles['pdp-content-well__row']}>
          {miniBoxes?.map((miniBox) => (
            <PdpValueTile
              data={miniBox}
              key={miniBox.id}
              openModal={setIsModalOpen}
              setModalContent={setModalContent}
            />
          ))}
        </div>
      </div>

      {pdpQuickLook && (
        <ARQuickLook
          data={pdpQuickLook}
          customClass={styles['pdp-content-well__quick-look']}
        />
      )}

      {modalContent && (
        <PDPContentModal
          closeModal={() => setIsModalOpen(false)}
          key={modalKey}
          data={modalContent}
          isOpen={isModalOpen}
          fullscreen={isMobile || isTablet}
          bikePrice={promo?.price || parsedPrice}
        />
      )}
    </div>
  );
};

export default PDPContentWell;
